import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RoundView from './RoundView.jsx';
import GameSummary from './GameSummary.jsx';
import Leaderboard from './Leaderboard.jsx';
import { homeHref } from '../siteHome.mjs';
import SkyBackdrop from './SkyBackdrop.jsx';
import SettingsMenu, { useSettings } from './SettingsMenu.jsx';
import TutorialView from './tutorial/TutorialView.jsx';
import { generateRound, generateEventRound } from './roundGenerator.mjs';
import { eventForDate } from './events.mjs';
import { rngForRound } from './daily.mjs';
import { playedOn, postedOn, recordPlay, roundsOf, distancesOf, markSubmitted } from './playRecord.mjs';
import { cleanName, getPlayerName, rememberPlayerName, forgetPlayerName } from './playerName.mjs';
import { distanceEmoji } from './shareText.mjs';
import { formatDistance } from './format.mjs';
import { KM_PER_MILE } from './settings.mjs';
import {
  scoreRound,
  scoreForDistance,
  TIERS,
  ROUNDS_PER_GAME,
  MAX_ROUND_SCORE
} from './scoring.mjs';
import { pathForView, viewFromPath, modeFromSearch } from './routes.mjs';

import starsUrl from '../../assets/starguessr-stars.json?url';
import landUrl from '../../assets/starguessr-land.json?url';
import constellationsUrl from '../../assets/starguessr-constellations.json?url';
import milkyWayUrl from '../../assets/starguessr-milkyway.jpg';
import moonUrl from '../../assets/starguessr-moon.jpg';

function loadTexture(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(image, 0, 0);
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      resolve({ data, width: canvas.width, height: canvas.height });
    };
    // The Milky Way is decoration on top of a working sky: if it cannot be
    // decoded, the round is still perfectly playable without it.
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

export default function StarguessrApp() {
  const [assets, setAssets] = useState(null);
  const [game, setGame] = useState(null);
  const [boardTier, setBoardTier] = useState('easy');
  const [boardKey, setBoardKey] = useState(0);
  const [submission, setSubmission] = useState({ status: 'idle', error: null, name: null });
  /*
   * The name this browser posts under, mirrored into state so the results screen
   * re-renders when it changes. localStorage is still where it lives; this is a
   * copy, read once on mount and written through on every change.
   */
  const [boardName, setBoardName] = useState(() => getPlayerName());
  /*
   * Whether this finished game has already had its one automatic submission.
   *
   * A ref and not the submission status, because a failed submission returns to
   * `idle` and status alone would retry on every render for as long as the
   * results stayed open. Declared up here with the state it guards, since the
   * two callbacks that reset it are defined further down.
   */
  const autoSubmitted = useRef(false);
  const [teaching, setTeaching] = useState(false);
  // The rules, which are an overlay rather than a disclosure box: the start
  // screen is pinned to the viewport, and there is nowhere for it to expand to.
  const [howOpen, setHowOpen] = useState(false);
  /*
   * Settings live above every screen rather than on the start page alone: star
   * brightness is a thing you want to change while looking at the sky you are
   * judging it by, not one screen away from it.
   */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settings = useSettings();
  // What this browser has already played today, so the start page can say so.
  const [played, setPlayed] = useState(() => playedOn());

  /*
   * The route the address bar is asking for and the app has not opened yet.
   * Cleared once applied, so it only ever fires on a real navigation: a cold
   * load, a back button, or a link inside the app.
   */
  const [pending, setPending] = useState(() => ({
    ...viewFromPath(window.location.pathname),
    mode: modeFromSearch(window.location.search)
  }));

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(starsUrl).then((r) => r.json()),
      fetch(landUrl).then((r) => r.json()),
      fetch(constellationsUrl).then((r) => r.json()),
      loadTexture(milkyWayUrl),
      loadTexture(moonUrl)
    ]).then(([catalog, rings, figures, texture, moonTexture]) => {
      if (!cancelled) setAssets({ catalog, rings, figures, texture, moonTexture });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Escape closes the rules, which is the one thing every reader tries first.
  useEffect(() => {
    if (!howOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setHowOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [howOpen]);

  /*
   * One game a day, the same for everyone.
   *
   * Each round draws from a generator seeded off the calendar date and its own
   * index, so today's three places and hours are fixed: two people playing today
   * get the same game and their scores mean the same thing. The last round is
   * the historical one when the corpus has an entry for the date, which was
   * already fixed by the calendar — this makes the other two match.
   */
  /*
   * Everywhere else this game will visit, so a round can be kept clear of them.
   * The historical round's place is fixed and known before anything is dealt, so
   * it goes in from the start rather than only once it has been played.
   */
  const avoidFor = useCallback((chosenTier, index, mode, played) => {
    const places = played.map((entry) => ({ lat: entry.round.lat, lon: entry.round.lon }));
    const event = mode === 'random' ? null : eventForDate(new Date(), chosenTier);
    if (event && index !== ROUNDS_PER_GAME - 1) places.push({ lat: event.lat, lon: event.lon });
    return places;
  }, []);

  const dealRound = useCallback(
    (chosenTier, index, mode = 'daily', avoid = []) => {
      /*
       * A practice game is freshly random and carries no historical round: that
       * round is the daily game's payoff and has already been played by anyone
       * who is here. Its rng is Math.random rather than the day's seed, which is
       * what makes it different every time.
       */
      const random = mode === 'random';
      const rng = random ? Math.random : rngForRound(new Date(), index, chosenTier);
      const event = !random && index === ROUNDS_PER_GAME - 1 ? eventForDate(new Date(), chosenTier) : null;
      const historical =
        event &&
        generateEventRound({
          tier: chosenTier,
          event,
          catalog: assets.catalog,
          figures: assets.figures,
          rng
        });
      return (
        historical ||
        generateRound({ tier: chosenTier, catalog: assets.catalog, figures: assets.figures, rng, avoid })
      );
    },
    [assets]
  );

  const startGame = useCallback(
    (chosenTier, mode = 'daily') => {
      if (!assets) return;
      const round = dealRound(chosenTier, 0, mode, avoidFor(chosenTier, 0, mode, []));
      if (!round) return;
      setBoardTier(chosenTier);
      setSubmission({ status: 'idle', error: null, name: null });
      // A new game gets its own automatic submission.
      autoSubmitted.current = false;
      setGame({ tier: chosenTier, mode, rounds: [], current: round, result: null });
    },
    [assets, dealRound, avoidFor]
  );

  /*
   * The stored daily result, rebuilt into the shape a finished game has so the
   * results screen renders it through the same component. Falls through to a
   * fresh daily game if this browser has not played one.
   */
  const showStoredResult = useCallback(
    (chosenTier) => {
      const stored = playedOn()[chosenTier];
      if (!stored) return startGame(chosenTier, 'daily');
      setBoardTier(chosenTier);
      /*
       * A stored result that never reached the board is worth one automatic
       * attempt too — the usual reason it is sitting here unsubmitted is that
       * the request failed, or the tab was closed before it finished.
       */
      setSubmission({
        status: stored.submitted ? 'done' : 'idle',
        error: null,
        name: stored.name || null
      });
      autoSubmitted.current = false;
      return setGame({
        tier: chosenTier,
        mode: 'daily',
        restored: true,
        rounds: roundsOf(stored),
        current: null,
        result: null
      });
    },
    [startGame]
  );

  /*
   * Apply whatever the URL is asking for, once the catalogue is in: a cold load
   * on /game/ has to wait for the stars before it can deal a round.
   */
  useEffect(() => {
    if (!pending || !assets) return;
    setPending(null);
    if (pending.view === 'tutorial') {
      setTeaching(true);
      setGame(null);
      return;
    }
    setTeaching(false);
    if (pending.view !== 'game') return setGame(null);
    if (pending.mode === 'result') return showStoredResult(pending.tier);
    return startGame(pending.tier, pending.mode);
  }, [pending, assets, startGame, showStoredResult]);

  // The back button, and anything else that moves through history.
  useEffect(() => {
    const onPop = () =>
      setPending({
        ...viewFromPath(window.location.pathname),
        mode: modeFromSearch(window.location.search)
      });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /*
   * In-app navigation. Pushes the address and asks for the view in one move, so
   * every route change goes through the same path whether it came from a click
   * or from the address bar.
   */
  const go = useCallback((view, tier, mode = 'daily') => {
    const path = pathForView(view, tier, mode);
    if (window.location.pathname + window.location.search !== path) {
      window.history.pushState({}, '', path);
    }
    setPending({ view, tier: tier || null, mode });
  }, []);

  // Plain links, so middle-click and open-in-new-tab still work.
  const linkTo = (view, tier, mode = 'daily') => ({
    href: pathForView(view, tier, mode),
    onClick: (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      go(view, tier, mode);
    }
  });

  const submitGuess = ({ guess }) => {
    const result = scoreRound({
      tier: game.tier,
      guessLat: guess.lat,
      guessLon: guess.lon,
      actualLat: game.current.lat,
      actualLon: game.current.lon
    });
    setGame((prev) => ({ ...prev, result, guess }));
  };

  const nextRound = () => {
    setGame((prev) => {
      const completed = [
        ...prev.rounds,
        { round: prev.current, guess: prev.guess, result: prev.result }
      ];
      if (completed.length === ROUNDS_PER_GAME) {
        /*
         * Remembered here rather than on the results screen, because this is the
         * one moment a game is definitely finished — the results screen can be
         * arrived at again by the back button.
         */
        if (prev.mode !== 'random') {
          recordPlay({
            tier: prev.tier,
            total: completed.reduce((sum, entry) => sum + entry.result.roundScore, 0),
            rounds: completed
          });
          setPlayed(playedOn());
        }
        return { ...prev, rounds: completed, current: null, result: null };
      }
      return {
        ...prev,
        rounds: completed,
        // The mode has to travel with every round, or a practice game silently
        // reverts to the day's places from round two onward.
        current: dealRound(
          prev.tier,
          completed.length,
          prev.mode,
          avoidFor(prev.tier, completed.length, prev.mode, completed)
        ),
        result: null,
        guess: null
      };
    });
  };

  const total = useMemo(
    () => (game ? game.rounds.reduce((sum, entry) => sum + entry.result.roundScore, 0) : 0),
    [game]
  );

  // Which screen is up. Declared here rather than beside the render because the
  // automatic submission below is keyed on the results appearing.
  const playing = Boolean(game && game.current) || teaching;
  const results = Boolean(game && !game.current);
  const start = !game && !teaching;

  const submitScore = useCallback(
    (rawName) => {
      const playerName = cleanName(rawName);
      if (!playerName || !game) return;
      /*
       * A practice game is not the day's game and never goes on a board.
       *
       * The results screen already withholds the form for one and the automatic
       * post already skips one, but the boards are keyed by day now, and a
       * practice round is generated for today like any other — so a stray call
       * from some future path would file it under today's puzzle, where it would
       * outrank people who played the real one. Cheaper to refuse here, once,
       * than to keep every caller honest.
       */
      if (game.mode === 'random') return;

      setSubmission({ status: 'saving', error: null, name: playerName });
      fetch('/api/starguessr/games', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerName,
          difficulty: game.tier,
          rounds: game.rounds.map((entry, index) => ({
            roundIndex: index,
            actualLat: entry.round.lat,
            actualLon: entry.round.lon,
            roundDate: entry.round.date.toISOString(),
            guessLat: entry.guess.lat,
            guessLon: entry.guess.lon
          }))
        })
      })
        /*
         * Show what the server actually said. Every rejection here carries a
         * reason — the wrong number of rounds, a bad tier, coordinates out of
         * range — and swallowing them all into "could not save that score" turns
         * a one-line diagnosis into a hunt. The generic line is the fallback for
         * a genuinely silent failure, like the API not being up.
         */
        .then(async (response) => {
          if (response.ok) return response.json();
          const reason = await response
            .json()
            .then((body) => body.error)
            .catch(() => null);
          throw new Error(reason || `The server refused it (${response.status}).`);
        })
        .then(() => {
          setSubmission({ status: 'done', error: null, name: playerName });
          /*
           * Remembered only now, on the far side of a success. A name the server
           * rejected — too long, or empty after trimming — would otherwise be
           * the one every future game silently posted under.
           */
          rememberPlayerName(playerName);
          setBoardName(playerName);
          markSubmitted(new Date(), game.tier, playerName);
          setPlayed(playedOn());
          setBoardTier(game.tier);
          setBoardKey((key) => key + 1);
        })
        .catch((error) => {
          setSubmission({
            status: 'idle',
            name: playerName,
            error: error.message ? `Could not save that score: ${error.message}` : 'Could not save that score.'
          });
        });
    },
    [game]
  );

  /*
   * Post the day's score without being asked, once this browser has a name.
   *
   * One automatic attempt per finished game — see `autoSubmitted` above for why
   * the guard is a ref. After that the form is there, filled in, and it is a
   * person's decision whether to try again.
   *
   * Nothing here is silent: the board names whichever name the score went up
   * under, so the first automatic submission is also the moment it is explained,
   * and there is a way to change it or stop.
   */
  useEffect(() => {
    if (!results || !game || game.mode === 'random') return;
    if (autoSubmitted.current || submission.status !== 'idle') return;
    if (!boardName) return;
    /*
     * One automatic post per tier per day.
     *
     * Nothing stops the daily being replayed — the tier card offers random
     * places once it is spent, but the address still deals a fresh daily game —
     * and each replay is a new record with a new unsubmitted flag. Posting every
     * one of those without being asked would turn a replay into a column of
     * near-identical rows under one name. The second game still *can* be
     * submitted; it takes the button, which is the difference between a person
     * deciding and a page deciding.
     */
    if (postedOn()[game.tier]) return;
    autoSubmitted.current = true;
    submitScore(boardName);
  }, [results, game, submission.status, boardName, submitScore]);

  /*
   * Change the name future games post under, or — with an empty one — stop them
   * posting by themselves at all. Deliberately does not touch the score already
   * on the board: see the note in `Leaderboard.jsx`.
   */
  const changeName = useCallback((next) => {
    const clean = cleanName(next);
    if (clean) rememberPlayerName(clean);
    else forgetPlayerName();
    setBoardName(clean);
  }, []);

  /*
   * The two distances the scoring sentence quotes. They are a round thousand and
   * ten thousand of whatever unit the player reads in, converted to kilometres
   * for `scoreForDistance` — which, like everything else in scoring, only ever
   * sees kilometres. Quoting a flat "1,000 km" to someone reading miles was the
   * one place the units setting did not reach.
   */
  const near = settings.units === 'mi' ? KM_PER_MILE * 1000 : 1000;
  const far = near * 10;

  /*
   * The masthead, written once and placed twice. On the start screen it is the
   * centred title block; everywhere else it is the small bar along the top. Only
   * its styling differs, and the title is the way back out of a game or a lesson
   * either way.
   */
  const masthead = (
    <header className="sg-header">
      <h1>
        <a className="sg-brand" {...linkTo('start')}>StarGuessr</a>
      </h1>
      <p>
        You are somewhere on Earth, at night.
      </p>
      {/*
        The way into settings from everywhere that is not the start screen,
        which has its own link in the footer. Star brightness in particular is
        worth changing while looking at the sky you are judging it against.
      */}
      <button
        className="sg-gear"
        type="button"
        onClick={() => setSettingsOpen(true)}
        aria-label="Settings"
      >
        <span aria-hidden="true">⚙</span>
      </button>
      <a className="sg-home" href={homeHref()}>&larr; Side Quests</a>
    </header>
  );

  if (!assets) {
    return (
      <main className="sg-page is-start">
        <p className="sg-loading">Loading star catalogue…</p>
      </main>
    );
  }

  return (
    <main
      className={`sg-page${playing ? ' is-playing' : ''}${results ? ' is-results' : ''}${
        start ? ' is-start' : ''
      }${settings.palette === 'colourblind' ? ' is-colourblind' : ''}`}
    >
      {/*
        The real sky, behind the two screens that are not themselves a sky. It is
        drawn from the same catalogue the game uses, so the page's first
        impression is not a decoration that contradicts the claim underneath it.
        The round and the tutorial already show the real thing full size, so they
        do not get it; the results screen does, dimmed, because it is otherwise
        the one page in the quest with no sky on it at all.
      */}
      {(start || results) && assets && <SkyBackdrop catalog={assets.catalog} />}

      {start ? (
        <div className="sg-start">
          {masthead}

          <section className="sg-intro">
            <div className="sg-tiers">
              {/*
                Two choices, and they are the page. Each is a container rather
                than a link, with the links inside it: the played line is itself
                a link to that result, and an anchor cannot hold another one.
              */}
              {Object.values(TIERS).map((entry) => (
                <div key={entry.key} className="sg-tier">
                  <a
                    className="sg-tier-main"
                    {...linkTo('game', entry.key, played[entry.key] ? 'random' : 'daily')}
                  >
                    <span className="sg-tier-name">{entry.label}</span>
                    <span className="sg-tier-note">
                      {entry.assisted
                        ? 'Constellations and stars are marked, with a compass on the horizon.'
                        : 'No labels, no compass. Just you and the stars.'}
                    </span>
                    <span className="sg-tier-go">
                      {played[entry.key] ? 'Play random places' : 'Play daily challenge'} &rarr;
                    </span>
                  </a>

                  {played[entry.key] ? (
                    <a className="sg-tier-played" {...linkTo('game', entry.key, 'result')}>
                      <span className="sg-tier-played-moons" aria-hidden="true">
                        {distancesOf(played[entry.key]).map((km, i) => (
                          <span key={i}>{distanceEmoji(km)}</span>
                        ))}
                      </span>
                      Played today &middot;{' '}
                      <strong>{played[entry.key].total.toLocaleString()}</strong>
                    </a>
                  ) : null}
                </div>
              ))}
            </div>

            {/*
              The third thing you can do from here, and the one worth doing
              first if the sky means nothing to you yet. It sits under the two
              play buttons rather than down in the footer with the rules and the
              settings: those are reference, this is a way in.
            */}
            <a className="sg-learn" {...linkTo('tutorial')}>
              <span className="sg-learn-mark" aria-hidden="true">✦</span>
              Learn to read the sky
            </a>
          </section>
        </div>
      ) : (
        masthead
      )}

      {start && (
        <footer className="sg-start-foot">
          <button className="sg-how-open" type="button" onClick={() => setHowOpen(true)}>
            How to play
          </button>
          <button className="sg-how-open" type="button" onClick={() => setSettingsOpen(true)}>
            Settings
          </button>
        </footer>
      )}

      {settingsOpen && <SettingsMenu onClose={() => setSettingsOpen(false)} />}

      {teaching && (
        <TutorialView
          catalog={assets.catalog}
          figures={assets.figures}
          rings={assets.rings}
          onExit={() => go('start')}
          onPlay={() => go('game', 'easy')}
        />
      )}

      {start && howOpen && (
        /*
          A disclosure box would have to expand, and there is nowhere to expand
          to on a screen pinned to the viewport. So the rules come over the top
          instead, and scroll inside themselves if the window is short.
        */
        <div
          className="sg-how-veil"
          role="dialog"
          aria-modal="true"
          aria-label="How to play"
          onClick={() => setHowOpen(false)}
        >
          <div className="sg-how-sheet" onClick={(event) => event.stopPropagation()}>
            <button
              className="sg-how-close"
              type="button"
              onClick={() => setHowOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h2>How to play</h2>

            {/*
              Only the rules live here. How to actually read a sky is the
              tutorial's job, and it can move the sky while it explains, which no
              amount of prose in a box can.
            */}
            <div className="sg-how-grid">
              <p>
                Each game is {ROUNDS_PER_GAME} rounds. In every round you are dropped somewhere on
                Earth, after dark, and shown the sky as it actually looks from that spot at that moment. It can
                be anywhere — the middle of the Pacific or on land. When you're ready, drop a pin on the map and submit.
              </p>
              <p>Round 3 of the daily challenge is a historic round. You'll see the sky as it appeared on a famous event of today's date.
                The star locations are almost identical, but the planets and moon will be different.
              </p>
              <p>
                In Easy the constellations and stars are named, and you're given a compass on the horizon. 
                In Hard you only have the stars. The labels will appear after your guess is locked in.
              </p>
            </div>

            <p className="sg-how-more">
              How do you navigate with the stars as your guide, as seafarers, desert wanderers, and even astronauts have
              done for thousands of years? Check out{' '}
              <a className="sg-how-link" {...linkTo('tutorial')}>
                the tutorial
              </a>{' '}
              to start your journey.
            </p>
          </div>
        </div>
      )}

      {game && game.current && !teaching && (
        <RoundView
          key={game.rounds.length}
          round={game.current}
          rings={assets.rings}
          texture={assets.texture}
          moonTexture={assets.moonTexture}
          roundIndex={game.rounds.length}
          runningTotal={total}
          result={game.result}
          onSubmitGuess={submitGuess}
          onNext={nextRound}
        />
      )}

      {results && (
        <div className="sg-results">
          <GameSummary
            tier={game.tier}
            rounds={game.rounds}
            total={total}
            rings={assets.rings}
            practice={game.mode === 'random'}
            /*
             * A fresh set of random places, not the start screen. The daily is
             * the same three rounds all day, so "play again" landing back on a
             * page whose only button replays the game you have just finished
             * read as the button being broken.
             */
            onPlayAgain={() => go('game', game.tier, 'random')}
          />
          <Leaderboard
            tier={boardTier}
            onChangeTier={setBoardTier}
            refreshKey={boardKey}
            playedTier={game.tier}
            submission={submission}
            /* A practice game is not the day's game, so it is not ranked. */
            onSubmitScore={game.mode === 'random' ? null : submitScore}
            rememberedName={boardName}
            onChangeName={changeName}
          />
        </div>
      )}
    </main>
  );
}
